/** Clerk Auth の token が期限切れ・発行できない場合のエラー */
export class AuthTokenExpiredException extends Error {
  constructor() {
    super('Auth token expired')
    this.name = 'AuthTokenExpiredException'
  }
}

/** DB ファイルが破損していた場合のエラー */
export class CorruptedDatabaseFileException extends Error {
  /**
   * DB破損例外を生成する。
   * @param details 調査に必要な範囲へ制限した補足情報。
   */
  constructor(details?: string) {
    super(
      details === undefined
        ? 'Database file is corrupted'
        : `Database file is corrupted: ${details}`,
    )
    this.name = 'CorruptedDatabaseFileException'
  }
}

/**
 * 操作対象の portfolio が開いている portfolio と一致しなくなった場合のエラー。
 * portfolio 切り替え後に古い portfolio へ読み書きを継続することを防ぐために使う。
 */
export class PortfolioChangedException extends Error {
  /** 操作対象として期待していた portfolio ID */
  readonly expectedPortfolioId: string
  /** 検知時点で開いている portfolio ID。未設定なら undefined */
  readonly currentPortfolioId: string | undefined

  /**
   * portfolio 不一致例外を生成する。
   * @param params 期待した portfolio ID と検知時点の portfolio ID を受け取る。
   */
  constructor(params: {
    readonly expectedPortfolioId: string
    readonly currentPortfolioId: string | undefined
  }) {
    const { expectedPortfolioId, currentPortfolioId } = params
    super(
      `The opened portfolio has been changed: expected ${expectedPortfolioId}, current ${currentPortfolioId ?? 'none'}`,
    )
    this.name = 'PortfolioChangedException'
    this.expectedPortfolioId = expectedPortfolioId
    this.currentPortfolioId = currentPortfolioId
  }
}

/** アップロードしたファイルが不正な形式の場合のエラー */
export class InvalidFileFormatException extends Error {
  constructor() {
    super('Invalid file format')
    this.name = 'InvalidFileFormatException'
  }
}

/** Readonly mode で DB に書き込みを行おうとした場合のエラー */
export class UnwritableDatabaseException extends Error {
  constructor() {
    super('Database is in readonly mode')
    this.name = 'UnwritableDatabaseException'
  }
}

/** 取引所データ同期に失敗した場合のエラー */
export class FailedToAnalyzeExchangeException extends Error {
  readonly exchangeName: string
  constructor(error: unknown, exchangeName: string) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to analyze exchange data of ${exchangeName}\n\n${error.message}`
    } else {
      message = `Failed to analyze exchange data of ${exchangeName}`
    }
    super(message)
    this.exchangeName = exchangeName
  }
}

export class FailedToAnalyzeExchangeApiException extends Error {
  readonly exchangeName: string
  readonly apiId: string
  /** 同期失敗の原因となった元の例外。原因別の案内表示に使う */
  readonly cause: unknown
  constructor(error: unknown, exchangeName: string, apiId: string) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to analyze exchange data of ${exchangeName}.${apiId}\n\n${error.message}`
    } else {
      message = `Failed to analyze exchange data of ${exchangeName}.${apiId}`
    }
    super(message)
    this.exchangeName = exchangeName
    this.apiId = apiId
    this.cause = error
  }
}

export type FailedEvmAddressChainDetail = {
  address: string
  failedChains: string[]
}

export class FailedToRegisterEvmAddressException extends Error {
  readonly details: FailedEvmAddressChainDetail[]
  readonly addresses: string[]
  constructor(error: unknown, details: FailedEvmAddressChainDetail[]) {
    const addresses = details.map(({ address }) => address)
    let message: string
    if (error instanceof Error) {
      message = `Failed to register EVM addresses: ${addresses.join(', ')}\n\n${error.message}`
    } else {
      message = `Failed to register EVM addresses: ${addresses.join(', ')}`
    }
    super(message)
    this.details = details
    this.addresses = addresses
  }
}

/** EVM Chain データ同期に失敗した場合のエラー */
export class FailedToAnalyzeEvmChainException extends Error {
  readonly chainName: string
  constructor(error: unknown, chainName: string) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to analyze transaction of ${chainName}\n\n${error.message}`
    } else {
      message = `Failed to analyze transaction of ${chainName}`
    }
    super(message)
    this.chainName = chainName
  }
}

/** EVM Chain で index data の取得に失敗した場合のエラー */
export class FailedToFetchEvmIndexDataException extends Error {
  readonly chainName: string
  readonly address: string
  constructor(error: unknown, chainName: string, address: string) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to fetch index data of ${chainName}.${address}\n\n${error.message}`
    } else {
      message = `Failed to fetch index data of ${chainName}.${address}`
    }
    super(message)
    this.chainName = chainName
    this.address = address
  }
}

/** FailedToFetchEvmTxsException の message に含める hash の最大件数 */
const FAILED_TO_FETCH_EVM_TXS_SAMPLE_HASH_COUNT = 5

/**
 * EVM Chain で transaction data の取得に失敗した場合のエラー。
 * 同期 1 回で失敗した tx をチェーン単位でまとめて保持する。
 */
export class FailedToFetchEvmTxsException extends Error {
  readonly chainName: string
  readonly hashes: readonly string[]
  /**
   * @param params - 生成パラメータ
   * @param params.chainName - 取得に失敗したチェーン名
   * @param params.errors - 取得に失敗した tx hash と原因エラーの組 (1 件以上)
   */
  constructor({
    chainName,
    errors,
  }: {
    readonly chainName: string
    readonly errors: ReadonlyMap<string, unknown>
  }) {
    const hashes = [...errors.keys()]
    const sampleHashes = hashes.slice(
      0,
      FAILED_TO_FETCH_EVM_TXS_SAMPLE_HASH_COUNT,
    )
    const firstError = errors.values().next().value
    const header = `Failed to fetch ${hashes.length} transactions of ${chainName} (${sampleHashes.join(', ')}${hashes.length > sampleHashes.length ? ', ...' : ''})`
    super(
      firstError instanceof Error
        ? `${header}\n\n${firstError.message}`
        : header,
    )
    this.chainName = chainName
    this.hashes = hashes
  }
}

/** EVM Chain で transaction data の解析に失敗した場合のエラー */
export class FailedToAnalyzeEvmTxException extends Error {
  readonly chainName: string
  readonly hash: string
  constructor(error: unknown, chainName: string, hash: string) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to analyze transaction of ${chainName}.${hash}\n\n${error.message}`
    } else {
      message = `Failed to analyze transaction of ${chainName}.${hash}`
    }
    super(message)
    this.chainName = chainName
    this.hash = hash
  }
}

/** Polkadot Chain データ同期に失敗した場合のエラー */
export class FailedToAnalyzePolkadotChainException extends Error {
  readonly chainName: string
  constructor(error: unknown, chainName: string) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to analyze transaction of ${chainName}\n\n${error.message}`
    } else {
      message = `Failed to analyze transaction of ${chainName}`
    }
    super(message)
    this.chainName = chainName
  }
}

/** Polkadot Chain で index data の取得に失敗した場合のエラー */
export class FailedToFetchPolkadotIndexDataException extends Error {
  readonly chainName: string
  readonly address: string
  constructor(error: unknown, chainName: string, address: string) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to fetch index data of ${chainName}.${address}\n\n${error.message}`
    } else {
      message = `Failed to fetch index data of ${chainName}.${address}`
    }
    super(message)
    this.chainName = chainName
    this.address = address
  }
}

/** Polkadot Chain で block data の取得に失敗した場合のエラー */
export class FailedToFetchPolkadotBlockDataException extends Error {
  readonly chainName: string
  readonly blockNumber: number
  constructor(error: unknown, chainName: string, blockNumber: number) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to fetch block data of ${chainName}.${blockNumber}\n\n${error.message}`
    } else {
      message = `Failed to fetch block data of ${chainName}.${blockNumber}`
    }
    super(message)
    this.chainName = chainName
    this.blockNumber = blockNumber
  }
}

/** Solana Chain データ同期に失敗した場合のエラー */
export class FailedToAnalyzeSolanaException extends Error {
  constructor(error: unknown) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to analyze Solana chain data\n\n${error.message}`
    } else {
      message = 'Failed to analyze Solana chain data'
    }
    super(message)
  }
}

/** Solana Chain transaction data の取得に失敗した場合のエラー */
export class FailedToFetchSolanaAddressException extends Error {
  readonly address: string
  constructor(error: unknown, address: string) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to fetch address data of Solana.${address}\n\n${error.message}`
    } else {
      message = `Failed to fetch transaction of Solana.${address}`
    }
    super(message)
    this.address = address
  }
}

/** Solana Chain transaction data の取得に失敗した場合のエラー */
export class FailedToAnalyzeSolanaTxException extends Error {
  readonly signature: string
  constructor(error: unknown, signature: string) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to analyze transaction of Solana.${signature}\n\n${error.message}`
    } else {
      message = `Failed to analyze transaction of Solana.${signature}`
    }
    super(message)
    this.signature = signature
  }
}

/** Solana Chain transaction data の取得に失敗した場合のエラー */
export class FailedToFetchSolanaTxException extends Error {
  readonly signature: string
  constructor(error: unknown, signature: string) {
    let message: string
    if (error instanceof Error) {
      message = `Failed to fetch transaction of Solana.${signature}\n\n${error.message}`
    } else {
      message = `Failed to fetch transaction of Solana.${signature}`
    }
    super(message)
    this.signature = signature
  }
}

export class RateLimitExceededException extends Error {
  readonly accountName: string

  constructor(error: unknown, accountName: string) {
    let message: string
    if (error instanceof Error) {
      message = `Rate limit exceeded for ${accountName}\n\n${error.message}`
    } else {
      message = `Rate limit exceeded for ${accountName}`
    }
    super(message)
    this.accountName = accountName
  }
}
